import React, { useState } from "react";
import AppHeader from "@/components/AppHeader";
import MobileNav from "@/components/MobileNav";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Bell, TestTube2, ExternalLink, ScrollText, Loader2 } from "lucide-react";

export default function SettingsPage() {
  const { settings, save } = useSettings();
  const [webhook, setWebhook] = useState(settings.discord_webhook_url || "");
  const [displayName, setDisplayName] = useState(settings.display_name || "");
  const [saving, setSaving] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [testing, setTesting] = useState(false);

  React.useEffect(() => { setWebhook(settings.discord_webhook_url || ""); }, [settings.discord_webhook_url]);
  React.useEffect(() => { setDisplayName(settings.display_name || ""); }, [settings.display_name]);

  const saveDisplayName = async () => {
    setSavingName(true);
    try {
      await save({ display_name: displayName.trim() || null });
      toast.success("Display name updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save display name");
    } finally { setSavingName(false); }
  };

  const saveWebhook = async () => {
    setSaving(true);
    try {
      await save({ discord_webhook_url: webhook || null });
      toast.success(webhook ? "Discord webhook saved" : "Discord webhook cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const toggleNotifications = async (v) => {
    try {
      await save({ notifications_enabled: v });
      toast.success(v ? "Notifications enabled" : "Notifications paused");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  const setOddsFormat = async (fmt) => {
    try {
      await save({ odds_format: fmt });
      toast.success(`Odds format: ${fmt}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setCurrency = async (cur) => {
    try {
      await save({ currency: cur });
      toast.success(`Currency: ${cur}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      await api.post("/settings/discord/test");
      toast.success("Test message sent to Discord");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0D14] pb-24 md:pb-8">
      <AppHeader />
      <MobileNav />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-emerald-400">Preferences</div>
          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            Settings
          </h1>
        </div>

        {/* Profile card */}
        <section className="rounded-xl bg-[#121824] border border-white/10 p-5 space-y-4">
          <div>
            <h2 className="text-lg font-bold uppercase tracking-wide text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Profile</h2>
            <p className="text-xs text-slate-400 mt-1">Your display name identifies you in Discord alerts and shared views. Your email is never shown publicly.</p>
          </div>
          <div>
            <Label htmlFor="dn" className="text-xs uppercase tracking-widest text-slate-400">Display name</Label>
            <div className="flex flex-col sm:flex-row gap-2 mt-1">
              <Input
                id="dn"
                data-testid="display-name-input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Dave"
                maxLength={30}
                className="bg-[#0E1420] border-white/10 text-white flex-1"
              />
              <Button
                data-testid="save-display-name-button"
                onClick={saveDisplayName}
                disabled={savingName}
                className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-bold uppercase tracking-wider"
              >
                {savingName ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Save
              </Button>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">2–30 characters. Letters, numbers, spaces, and _ . - ' allowed.</p>
          </div>
        </section>

        {/* Notifications card */}
        <section className="rounded-xl bg-[#121824] border border-white/10 p-5 space-y-5">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-emerald-400" />
            <h2 className="text-lg font-bold uppercase tracking-wide text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Live Notifications</h2>
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg bg-[#0E1420] border border-white/5">
            <div>
              <div className="text-sm font-semibold text-white">Global notifications</div>
              <div className="text-xs text-slate-400">Turn off to pause all Discord alerts for every slip.</div>
            </div>
            <Switch
              data-testid="notifications-toggle"
              checked={!!settings.notifications_enabled}
              onCheckedChange={toggleNotifications}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="webhook" className="text-xs uppercase tracking-widest text-slate-400">Discord webhook URL</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                id="webhook"
                data-testid="discord-webhook-input"
                value={webhook}
                onChange={(e) => setWebhook(e.target.value)}
                placeholder="https://discord.com/api/webhooks/..."
                className="bg-[#0E1420] border-white/10 text-white flex-1"
              />
              <Button
                data-testid="save-webhook-button"
                onClick={saveWebhook}
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-bold uppercase tracking-wider"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Save
              </Button>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <a
                href="https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-400 hover:underline inline-flex items-center gap-1"
              >
                How to create a Discord webhook <ExternalLink className="h-3 w-3" />
              </a>
              <Button
                data-testid="send-test-button"
                variant="outline"
                size="sm"
                onClick={runTest}
                disabled={testing || !settings.discord_webhook_url}
                className="border-white/10 text-slate-200 bg-white/5 hover:bg-white/10"
              >
                {testing ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <TestTube2 className="h-3 w-3 mr-2" />} Send test
              </Button>
            </div>
          </div>

          <Link
            to="/notifications"
            data-testid="notifications-history-link"
            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
          >
            <ScrollText className="h-4 w-4" /> View notification history →
          </Link>
        </section>

        {/* Display card */}
        <section className="rounded-xl bg-[#121824] border border-white/10 p-5 space-y-5">
          <h2 className="text-lg font-bold uppercase tracking-wide text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Display</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-400">Odds format</Label>
              <Select value={settings.odds_format} onValueChange={setOddsFormat}>
                <SelectTrigger data-testid="odds-format-select" className="bg-[#0E1420] border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121824] border-white/10 text-white">
                  <SelectItem value="decimal">Decimal (UK) — 1.83</SelectItem>
                  <SelectItem value="fractional">Fractional — 5/6</SelectItem>
                  <SelectItem value="american">American — -120</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Underlying bets stay accurate — this only changes display.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-400">Currency</Label>
              <Select value={settings.currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="currency-select" className="bg-[#0E1420] border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121824] border-white/10 text-white">
                  <SelectItem value="GBP">GBP (£) — Default</SelectItem>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
